/**
 * Lender submissions (docs/PLAN.md §4 PR 12, PRD §6 "Submission status", §9 "Lender adapter",
 * AC12). A submission is one attempt to get one package in front of one partner. It is a
 * different record from the package it carries: a package exists as soon as Tex builds it, a
 * submission exists once Tex decides to send it, and only an attested or adapter-confirmed
 * dispatch moves the case to `lender_review`.
 *
 *   prepared → dispatched → acknowledged → decision_received (PR 13)
 *   prepared | dispatched | acknowledged → delivery_unknown → acknowledged | delivery_failed
 *   prepared | dispatched | delivery_unknown → delivery_failed   (an attempt that did not / may not have landed)
 *   prepared | dispatched | acknowledged | delivery_unknown → withdrawn
 *   dispatched | acknowledged | delivery_unknown → superseded  (by dispatching a newer package)
 *
 * `manual_portal` is first-class: the operator exports the package, uploads it through the
 * partner's approved channel, then attests when/where it went. Exporting is never dispatching.
 * `mock` is synthetic, DB-guaranteed impossible against a live partner, and exists so the whole
 * flow can be rehearsed. `api` names a future live adapter; none ships in P0.
 */

import type { SubmissionMethod } from './api-types'
import type { PackageBlocker } from './packages'

export const SUBMISSION_METHODS: readonly SubmissionMethod[] = ['manual_portal', 'mock', 'api']

export const SUBMISSION_STATUSES = [
  'prepared',
  'dispatched',
  'acknowledged',
  'decision_received',
  'delivery_failed',
  'delivery_unknown',
  'withdrawn',
  'superseded',
] as const
export type SubmissionStatus = (typeof SUBMISSION_STATUSES)[number]

/** Statuses in which a submission still counts as "in front of the partner" or about to be. */
export const ACTIVE_SUBMISSION_STATUSES: readonly SubmissionStatus[] = ['prepared', 'dispatched', 'acknowledged', 'delivery_unknown', 'decision_received']
export const isActiveSubmissionStatus = (s: SubmissionStatus): boolean => ACTIVE_SUBMISSION_STATUSES.includes(s)

/** Statuses after which the partner may actually hold the package (so the case may sit in `lender_review`). */
export const SENT_SUBMISSION_STATUSES: readonly SubmissionStatus[] = ['dispatched', 'acknowledged', 'delivery_unknown', 'decision_received']

/**
 * What an adapter can actually do, so the UI never implies connectivity that does not exist.
 * `manual_portal`: nothing automated, attestation required. `mock`: automated but synthetic.
 */
export interface AdapterCapabilities {
  method: SubmissionMethod
  /** `dispatch` sends the package itself; false means the operator sends it and attests. */
  automated_dispatch: boolean
  /** `poll` can ask the partner for delivery status; false means "ask the operator to reconcile". */
  automated_status: boolean
  /** Dispatch needs an operator attestation (sent time, channel, note). */
  requires_attestation: boolean
}

export const MANUAL_DISPATCH_CHANNELS = ['partner_portal', 'secure_email', 'other'] as const
export type ManualDispatchChannel = (typeof MANUAL_DISPATCH_CHANNELS)[number]

/** The operator's statement of how a manual package went out. Stored verbatim; audited. */
export interface DispatchAttestationView {
  sent_at: string
  channel: ManualDispatchChannel
  /** Where within the channel: portal name, mailbox, courier. Never credentials. */
  destination: string
  note: string
  attested_by_user_id: string
  attested_at: string
}

/**
 * Why a prepared submission cannot be sent right now. Everything the package planner would
 * refuse to *build* today (`PackageBlocker`: review state, outstanding information requests,
 * consent, partner/program windows, unclean or missing documents) blocks a dispatch too, plus
 * the submission-specific reasons below.
 */
export type SubmissionBlocker =
  | PackageBlocker
  | { kind: 'package_stale'; package_revision_id: string; current_revision_id: string | null }
  | { kind: 'review_superseded'; package_review_id: string; latest_review_id: string }
  | { kind: 'program_test_only_live_partner'; program_version_id: string }
  | { kind: 'document_changed'; document_id: string; document_type: string }
  | { kind: 'adapter_not_available'; method: SubmissionMethod }
  | { kind: 'live_submissions_disabled' }
  | { kind: 'active_submission_exists'; submission_id: string; status: SubmissionStatus }
  /** A routing fan-out's per-partner step was refused by the underlying command; `code` is that command's error code. */
  | { kind: 'command_refused'; code: string; reason: string | null }

export interface LenderSubmissionView {
  id: string
  case_id: string
  package_id: string
  partner_id: string
  partner_display_name: string
  program_version_id: string
  manifest_sha256: string
  method: SubmissionMethod
  synthetic: boolean
  status: SubmissionStatus
  capabilities: AdapterCapabilities
  attempt_count: number
  last_attempt_at: string | null
  dispatched_at: string | null
  dispatched_by_user_id: string | null
  dispatch_attestation: DispatchAttestationView | null
  external_reference: string | null
  acknowledged_at: string | null
  delivery_evidence_document_id: string | null
  failure_reason: string | null
  supersedes_submission_id: string | null
  superseded_by_submission_id: string | null
  withdrawn_at: string | null
  withdrawn_reason: string | null
  created_at: string
  updated_at: string
}

export interface LenderSubmissionListResponse {
  submissions: LenderSubmissionView[]
}

/** What `dispatch` would run into right now, before anyone attests anything. */
export interface SubmissionReadinessView {
  submission_id: string
  blockers: SubmissionBlocker[]
}

/** `POST /cases/:id/submissions` */
export interface PrepareSubmissionBody {
  package_id: string
  /** Required when an earlier submission to the same partner is still active; it is marked superseded once this one is dispatched. */
  supersedes_submission_id?: string
}

/** `POST /cases/:id/submissions/:submissionId/dispatch` — `manual_portal` needs the attestation; `mock` takes an empty body. */
export interface DispatchSubmissionBody {
  attestation?: {
    sent_at: string
    channel: ManualDispatchChannel
    destination: string
    external_reference?: string
    evidence_document_id?: string
    note: string
  }
}

export const RECONCILE_OUTCOMES = ['acknowledged', 'delivery_unknown', 'delivery_failed'] as const
export type ReconcileOutcome = (typeof RECONCILE_OUTCOMES)[number]

/** `POST /cases/:id/submissions/:submissionId/reconcile` — the operator says what the partner told them. */
export interface ReconcileSubmissionBody {
  outcome: ReconcileOutcome
  note: string
  external_reference?: string
  evidence_document_id?: string
  /** Partner-stated time, when known; otherwise Tex's recording time is used. */
  occurred_at?: string
}

/** `POST /cases/:id/submissions/:submissionId/withdraw` */
export interface WithdrawSubmissionBody {
  reason: string
}
